/**
 * @fileoverview WebSocket types for Kick.com chat events
 * @version 1.0.0
 * @author Kick Flow Team
 */

/**
 * WebSocket connection configuration
 */
export interface WebSocketConfig {
	/** Chat room ID to connect to */
	chatroomId: number;
	/** Whether to auto-reconnect on connection loss */
	autoReconnect?: boolean;
	/** Reconnection delay in milliseconds */
	reconnectDelay?: number;
	/** Maximum reconnection attempts */
	maxReconnectAttempts?: number;
	/** Enable debug logging */
	debug?: boolean;
}

/**
 * WebSocket connection states
 */
export enum WebSocketState {
	CONNECTING = 'connecting',
	CONNECTED = 'connected',
	DISCONNECTED = 'disconnected',
	RECONNECTING = 'reconnecting',
	ERROR = 'error'
}

/**
 * Pusher protocol event structure
 */
export interface PusherEvent {
	event: string;
	data?: any;
	channel?: string;
}

/**
 * Chat message from WebSocket
 */
export interface ChatMessage {
	id: string;
	chatroom_id: number;
	content: string;
	type: string;
	created_at: string;
	sender: {
		id: number;
		username: string;
		slug: string;
		identity?: {
			color?: string;
			badges?: Badge[];
		};
	};
	metadata?: {
		original_sender?: {
			id: number;
			username: string;
		};
		original_message?: {
			id: string;
			content: string;
		};
	};
}

/**
 * User badge information
 */
export interface Badge {
	type: string;
	text: string;
	count?: number;
}

/**
 * Chat user joined/left event
 */
export interface ChatUserEvent {
	username: string;
	slug: string;
	id: number;
	chatroom_id: number;
}

/**
 * Chatroom updated event
 */
export interface ChatroomUpdatedEvent {
	id: number;
	chatter_count: number;
	slow_mode: boolean;
	subscribers_mode: boolean;
	followers_mode: boolean;
	emotes_mode: boolean;
	message_interval: number;
	following_min_duration: number;
}

/**
 * Stream host event
 */
export interface StreamHostEvent {
	host: {
		id: number;
		username: string;
		slug: string;
	};
	number_viewers: number;
}

/**
 * Subscription event
 */
export interface SubscriptionEvent {
	username: string;
	channel_name: string;
	months: number;
}

/**
 * Gift subscription event
 */
export interface GiftSubscriptionEvent {
	gifter_username: string;
	gifted_usernames: string[];
	months: number;
}

/**
 * WebSocket event types - using a simpler interface for EventEmitter compatibility
 */
export interface WebSocketEventMap {
	// Connection events
	connect: [];
	disconnect: [reason?: string];
	error: [error: Error];
	reconnect: [attempt: number];
	reconnect_failed: [];
	state_change: [state: WebSocketState];

	// Chat events
	message: [message: ChatMessage];
	user_joined: [user: ChatUserEvent];
	user_left: [user: ChatUserEvent];
	chatroom_updated: [chatroom: ChatroomUpdatedEvent];
	stream_host: [host: StreamHostEvent];
	subscription: [subscription: SubscriptionEvent];
	gift_subscription: [gift: GiftSubscriptionEvent];

	// Raw events (for debugging or custom handling)
	raw_event: [event: PusherEvent];
}

/**
 * WebSocket connection statistics
 */
export interface WebSocketStats {
	/** Current connection state */
	state: WebSocketState;
	/** Total messages received */
	messagesReceived: number;
	/** Connection uptime in milliseconds */
	uptime: number;
	/** Number of reconnection attempts */
	reconnectAttempts: number;
	/** Last error message */
	lastError?: string;
	/** Connection established at */
	connectedAt?: Date;
}
